import React, { useState, useEffect } from 'react';
import { 
  Receipt, Plus, Search, Clock, CheckCircle2, XCircle,
  Plane, Heart, Monitor, GraduationCap, Utensils, FileText,
  Upload, Paperclip, DollarSign
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { db } from '../../../lib/db';
import { formatCurrency, getPayrollStatusColor, formatPayrollStatus, getReimbursementCategoryInfo } from '../../../lib/payrollCalculations';
import { formatDate, formatTimeAgo } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';

const categoryIcons = {
  travel: Plane,
  medical: Heart,
  equipment: Monitor,
  training: GraduationCap,
  meals: Utensils,
  other: FileText
};

const categories = [
  { value: 'travel', label: 'Travel & Transport', icon: Plane },
  { value: 'medical', label: 'Medical Expenses', icon: Heart },
  { value: 'equipment', label: 'Equipment & Supplies', icon: Monitor },
  { value: 'training', label: 'Training & Education', icon: GraduationCap },
  { value: 'meals', label: 'Meals & Entertainment', icon: Utensils },
  { value: 'other', label: 'Other Expenses', icon: FileText }
];

export default function ReimbursementRequest() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [reimbursements, setReimbursements] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [newRequest, setNewRequest] = useState({
    category: '',
    amount: '',
    description: '',
    receiptNumber: '',
    attachment: null,
    attachmentName: ''
  });

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      const data = await db.reimbursements.where('userId').equals(user.id).toArray();
      setReimbursements(data.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      // Check file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only PDF, JPG, and PNG files are allowed');
        return;
      }
      // Convert to base64 for storage
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewRequest(prev => ({
          ...prev,
          attachment: reader.result,
          attachmentName: file.name
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!newRequest.category || !newRequest.amount) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await db.reimbursements.add({
        id: uuidv4(),
        userId: user.id,
        category: newRequest.category,
        amount: parseFloat(newRequest.amount),
        description: newRequest.description,
        receiptNumber: newRequest.receiptNumber,
        attachment: newRequest.attachment || null,
        attachmentName: newRequest.attachmentName || null,
        status: 'pending',
        approvedBy: null,
        submittedAt: new Date().toISOString(),
        createdAt: Date.now()
      });

      toast.success('Reimbursement request submitted');
      setShowAddModal(false);
      setNewRequest({ category: '', amount: '', description: '', receiptNumber: '', attachment: null, attachmentName: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  const filteredReimbursements = reimbursements.filter(r => {
    if (activeTab === 'all') return true;
    return r.status === activeTab;
  });

  const pendingAmount = reimbursements.filter(r => r.status === 'pending').reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const approvedAmount = reimbursements.filter(r => r.status === 'approved').reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const totalClaimed = reimbursements.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Reimbursements</h1>
          <p className="text-gray-500 mt-1">Submit and track expense claims</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> New Request
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" style={{height: '95vh', maxHeight: '95vh', overflowY: 'auto'}}>
            <DialogHeader>
              <DialogTitle>Submit Reimbursement</DialogTitle>
              <DialogDescription>
                Submit a new expense reimbursement request
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="category">Expense Category *</Label>
                <Select 
                  value={newRequest.category} 
                  onValueChange={(value) => setNewRequest(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          <cat.icon className="w-4 h-4" />
                          {cat.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="amount">Amount *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="amount"
                    type="number"
                    placeholder="0.00"
                    value={newRequest.amount}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, amount: e.target.value }))}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="receiptNumber">Receipt/Invoice Number</Label>
                <Input
                  id="receiptNumber"
                  placeholder="e.g., INV-12345"
                  value={newRequest.receiptNumber}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, receiptNumber: e.target.value }))}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the expense..."
                  value={newRequest.description}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div className="p-3 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <div className="text-center">
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">Attach Receipt (Optional)</p>
                  <p className="text-xs text-gray-400">PDF, JPG, PNG up to 5MB</p>
                  <input
                    type="file"
                    id="receipt-upload"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="receipt-upload">
                    <Button variant="outline" size="sm" className="mt-2" type="button" asChild>
                      <span>
                        <Paperclip className="w-4 h-4 mr-2" /> Choose File
                      </span>
                    </Button>
                  </label>
                  {newRequest.attachmentName && (
                    <div className="mt-2 flex items-center justify-center gap-2 text-sm text-emerald-600">
                      <CheckCircle2 className="w-4 h-4" />
                      {newRequest.attachmentName}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button 
                onClick={handleSubmit}
                className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
              >
                Submit Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Claimed</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalClaimed)}</p>
              </div>
              <Receipt className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(pendingAmount)}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(approvedAmount)}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {categories.map(cat => {
          const count = reimbursements.filter(r => r.category === cat.value).length;
          const amount = reimbursements.filter(r => r.category === cat.value).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
          const info = getReimbursementCategoryInfo(cat.value);
          
          return (
            <Card key={cat.value} className="border-0 shadow-sm">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${info.color}`}>
                    <cat.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{cat.label.split(' ')[0]}</p>
                    <p className="font-semibold text-gray-900">{count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Requests List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>My Requests</CardTitle>
              <CardDescription>Track status of your reimbursement claims</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="all">All ({reimbursements.length})</TabsTrigger>
              <TabsTrigger value="pending">
                Pending ({reimbursements.filter(r => r.status === 'pending').length})
              </TabsTrigger>
              <TabsTrigger value="approved">
                Approved ({reimbursements.filter(r => r.status === 'approved').length})
              </TabsTrigger>
              <TabsTrigger value="rejected">
                Rejected ({reimbursements.filter(r => r.status === 'rejected').length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab}>
              {filteredReimbursements.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p>No {activeTab === 'all' ? '' : activeTab} requests found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredReimbursements.map(request => {
                    const info = getReimbursementCategoryInfo(request.category);
                    const Icon = categoryIcons[request.category] || FileText;
                    
                    return (
                      <div 
                        key={request.id}
                        className={`flex flex-col p-4 border rounded-xl transition-all ${request.status === 'rejected' ? 'border-red-200 bg-red-50/30' : 'border-gray-100 hover:border-gray-200'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${info.color}`}>
                              <Icon className="w-6 h-6" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-semibold text-gray-900">{info.label}</p>
                                <Badge className={getPayrollStatusColor(request.status)}>
                                  {formatPayrollStatus(request.status)}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-500 line-clamp-1">
                                {request.description || 'No description'}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                Submitted {formatTimeAgo(request.submittedAt)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-gray-900">{formatCurrency(request.amount)}</p>
                            {request.receiptNumber && (
                              <p className="text-xs text-gray-500 mt-1">#{request.receiptNumber}</p>
                            )}
                          </div>
                        </div>
                        {request.status === 'rejected' && request.rejectionReason && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-red-700">Rejection Reason</p>
                                <p className="text-sm text-red-600 mt-1">{request.rejectionReason}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        {request.status === 'rejected' && !request.rejectionReason && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <XCircle className="w-4 h-4 text-red-500" />
                              <p className="text-sm text-red-600">This request was rejected. No reason provided.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card className="border-0 shadow-sm bg-blue-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Reimbursement Guidelines</p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                <li>Submit claims within 30 days of expense date</li>
                <li>Attach valid receipts or invoices for all claims</li>
                <li>Claims above $500 require manager approval</li>
                <li>Approved amounts will be added to your next payslip</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Add missing Info import at the top
const Info = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 16v-4"/>
    <path d="M12 8h.01"/>
  </svg>
);
